import React, { useState, useEffect } from 'react';
import './NotificationSetupModal.css';

const NotificationSetupModal = ({ isOpen, onClose, onRetry }) => {
  const [browserInfo, setBrowserInfo] = useState({
    browser: 'Unknown',
    os: 'Unknown',
    isMobile: false
  });

  useEffect(() => {
    const detectBrowserAndOS = () => {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      
      // Detect mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      // Detect browser
      let browser = 'Unknown';
      if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
        browser = 'Chrome';
      } else if (userAgent.includes('Firefox')) {
        browser = 'Firefox';
      } else if (userAgent.includes('Edg')) {
        browser = 'Edge';
      } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        browser = 'Safari';
      }
      
      // Detect OS
      let os = 'Unknown';
      if (platform.includes('Win')) {
        os = 'Windows';
      } else if (platform.includes('Mac')) {
        os = 'Mac';
      } else if (platform.includes('Linux')) {
        os = 'Linux';
      } else if (userAgent.includes('Android')) {
        os = 'Android';
      } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
        os = 'iOS';
      }
      
      setBrowserInfo({ browser, os, isMobile });
    };

    if (isOpen) {
      detectBrowserAndOS();
    }
  }, [isOpen]);

  const getInstructions = () => {
    const { browser, os, isMobile } = browserInfo;
    
    if (isMobile) {
      return {
        title: "Mobile Browser Notifications",
        steps: [
          " Mobile browsers often block notifications by default",
          " Try refreshing the page and enabling notifications when prompted",
          " Check your browser's notification settings in the menu",
          " Some mobile browsers don't support notifications reliably"
        ]
      };
    }

    if (browser === 'Chrome') {
      if (os === 'Windows') {
        return {
          title: "Chrome on Windows - Enable Notifications",
          steps: [
            " Windows Focus Assist: Press Win + A (Action Center)",
            " Look for 'Focus assist' at bottom, set to 'Off' (not 'Priority only')",
            " Windows Settings: Press Win + I → System → Notifications",
            " Find 'Google Chrome' in app list, make sure it's ON",
            " Click Chrome and verify 'Show notification banners' is enabled",
            " Chrome Settings: Go to chrome://settings/content/notifications",
            " Find intellegacy, remove it if present",
            " Return here and toggle notifications again"
          ]
        };
      } else if (os === 'Mac') {
        return {
          title: "Chrome on Mac - Enable Notifications",
          steps: [
            " System Preferences: Go to System Preferences → Notifications",
            " Find 'Google Chrome' and ensure notifications are enabled",
            " Chrome Settings: Go to chrome://settings/content/notifications",
            " Find intellegacy in blocked list if present",
            " Remove the blocked entry by clicking the trash icon",
            " Alternative: Click lock icon in address bar → Notifications → Allow",
            " Return here and toggle notifications again"
          ]
        };
      }
    }

    if (browser === 'Firefox') {
      return {
        title: "Firefox - Enable Notifications",
        steps: [
          " Click the shield or lock icon in the address bar",
          " Look for 'Receive Notifications' permission",
          " Change from 'Blocked' to 'Allow'",
          " Alternative: Go to about:preferences#privacy",
          " Scroll to 'Permissions' → 'Notifications' → 'Settings'",
          " Find this website and change to 'Allow'",
          " Return here and toggle notifications again"
        ]
      };
    }

    if (browser === 'Edge') {
      return {
        title: "Microsoft Edge - Enable Notifications",
        steps: [
          " Windows Settings: Press Win + I → System → Notifications",
          " Find 'Microsoft Edge' in app list, make sure it's ON",
          " Edge Settings: Go to edge://settings/content/notifications",
          " Find this website in the 'Block' list if present",
          " Remove it by clicking the menu (...) and selecting delete",
          " Alternative: Click lock icon in address bar → Notifications → Allow",
          " Return here and toggle notifications again"
        ]
      };
    }

    if (browser === 'Safari') {
      return {
        title: "Safari - Enable Notifications",
        steps: [
          " Safari Menu: Go to Safari → Preferences → Websites",
          " Click 'Notifications' in the left sidebar",
          " Find this website and change setting to 'Allow'",
          " If not listed, close preferences first",
          " Look for notification icon in Safari address bar",
          " Click it and select 'Allow'",
          " Return here and toggle notifications again"
        ]
      };
    }

    // Generic fallback
    return {
      title: "Enable Browser Notifications",
      steps: [
        " Check your system notification settings first",
        " Ensure your browser is allowed to show notifications",
        " Look for a lock or notification icon in your address bar",
        " Click it and select 'Allow' or 'Enable'",
        " Check your browser's notification settings page",
        " Remove this website from blocked list if present",
        " Return here and toggle notifications again"
      ]
    };
  };

  const instructions = getInstructions();

  if (!isOpen) return null;

  return (
    <div className="notification-modal-overlay">
      <div className="notification-modal">
        <div className="notification-modal-header">
          <h2>🔔 Notifications Blocked</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="notification-modal-content">
          <div className="browser-detection">
            <p><strong>Detected:</strong> {browserInfo.browser} on {browserInfo.os}</p>
          </div>
          
          <div className="issue-explanation">
            <p>Notifications are being blocked by a setting. Based on your device & browser, following these steps should resolve this:</p>
          </div>
          
          <div className="instructions">
            <h3>{instructions.title}</h3>
            <ol>
              {instructions.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
          
          <div className="modal-actions">
            <button className="retry-button" onClick={() => {
              onClose();
              setTimeout(onRetry, 500);
            }}>
              I've Fixed It - Try Again
            </button>
            <button className="later-button" onClick={onClose}>
              I'll Do This Later
            </button>
          </div>
          
          <div className="help-note">
            <p><strong>Still having trouble?</strong> <br/> Try refreshing the page or restarting your browser after making changes.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSetupModal;
